/** SUNTRONIX ONE — Tenant portal: My Assets & Warranty (+ lifecycle & support).
 *  Delivered serials with assigned user, location and warranty; asset lifecycle
 *  (Redeploy / Recycle / Retire / Repair) requests with timeline tracking; and
 *  service requests with a conversation thread. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  HardDrive, ShieldCheck, LifeBuoy, Loader2, User, MapPin, RotateCcw,
  Recycle, Repeat, Wrench, Archive, Clock, Send, ChevronRight, X,
} from "lucide-react";

const LC_TONE = {
  DEPLOYED: "bg-slate-100 text-slate-700", IN_USE: "bg-emerald-50 text-emerald-700",
  REDEPLOYED: "bg-sky-50 text-sky-700", RETURNED: "bg-amber-50 text-amber-700",
  IN_REPAIR: "bg-orange-50 text-orange-700", RETIRED: "bg-slate-200 text-slate-600",
  RECYCLED: "bg-lime-50 text-lime-700",
};
const REQ_TONE = {
  REQUESTED: "bg-amber-50 text-amber-700", APPROVED: "bg-sky-50 text-sky-700",
  SCHEDULED: "bg-indigo-50 text-indigo-700", IN_PROGRESS: "bg-blue-50 text-blue-700",
  COMPLETED: "bg-emerald-50 text-emerald-700", REJECTED: "bg-rose-50 text-rose-700",
  CANCELLED: "bg-slate-100 text-slate-500",
};
const TYPE_META = {
  REDEPLOY: { icon: Repeat, label: "Redeploy" }, RECYCLE: { icon: Recycle, label: "Recycle" },
  RETIRE: { icon: Archive, label: "Retire" }, REPAIR: { icon: Wrench, label: "Repair" },
};

// ---------------------------------------------------------------- lifecycle request
function LifecycleDialog({ asset, onClose, onDone }) {
  const [type, setType] = useState("REDEPLOY");
  const [f, setF] = useState({ reason: "", target_user: "", target_location: "", pickup_required: false, data_wipe_required: true, note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.reason.trim()) { toast.error("Please give a reason"); return; }
    if (type === "REDEPLOY" && !f.target_user.trim() && !f.target_location.trim()) { toast.error("Provide a target user or location"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tenant-catalogue/my-assets/lifecycle", {
        serial: asset.serial, request_type: type, ...f,
      });
      toast.success(`Request raised — ${data.request_no}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col" data-testid="lifecycle-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Repeat className="h-5 w-5 text-sky-600" />Asset lifecycle</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-500">{asset.product_name} · Serial {asset.serial}</div>
        <div className="space-y-3 mt-2 overflow-y-auto">
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(TYPE_META).map(([t, m]) => {
              const Icon = m.icon;
              return (
                <button key={t} onClick={() => setType(t)} data-testid={`lc-type-${t}`}
                  className={"flex flex-col items-center gap-1 py-2 rounded-md border text-[11px] " + (type === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300")}>
                  <Icon className="h-4 w-4" />{m.label}
                </button>
              );
            })}
          </div>
          {type === "REDEPLOY" && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[12px] text-slate-600">New user</label><Input value={f.target_user} onChange={(e) => setF({ ...f, target_user: e.target.value })} data-testid="lc-target-user" className="h-9 mt-1" placeholder="e.g. Jane Doe" /></div>
              <div><label className="text-[12px] text-slate-600">New location</label><Input value={f.target_location} onChange={(e) => setF({ ...f, target_location: e.target.value })} data-testid="lc-target-loc" className="h-9 mt-1" placeholder="e.g. Mumbai HQ" /></div>
            </div>
          )}
          <div><label className="text-[12px] text-slate-600">Reason *</label><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="lc-reason" className="text-sm mt-1" placeholder={type === "RECYCLE" ? "End of life / disposal" : type === "REPAIR" ? "Fault description" : "Reason for change"} /></div>
          <label className="flex items-center gap-2 text-[12px] text-slate-600"><input type="checkbox" checked={f.pickup_required} onChange={(e) => setF({ ...f, pickup_required: e.target.checked })} data-testid="lc-pickup" />Reverse pickup required</label>
          {(type === "RECYCLE" || type === "RETIRE") && (
            <label className="flex items-center gap-2 text-[12px] text-slate-600"><input type="checkbox" checked={f.data_wipe_required} onChange={(e) => setF({ ...f, data_wipe_required: e.target.checked })} data-testid="lc-wipe" />Secure data destruction (certificate)</label>
          )}
          <Button onClick={submit} disabled={busy} data-testid="lc-submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Submit request</Button>
          <p className="text-[11px] text-slate-400">Our team will review and coordinate {type === "RECYCLE" ? "certified recycling" : type === "REPAIR" ? "the repair" : "the change"}. Track progress under Lifecycle requests.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- asset detail + timeline
function AssetDetail({ serial, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/tenant-catalogue/my-assets/${serial}`).then(({ data }) => setD(data)).catch(() => setD(false)); }, [serial]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="asset-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><HardDrive className="h-5 w-5 text-sky-600" />Asset history</DialogTitle></DialogHeader>
        {!d ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div> : (
          <div className="space-y-4 overflow-y-auto">
            <div>
              <div className="text-sm font-medium text-slate-800">{d.asset.product_name}</div>
              <div className="text-[12px] text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                <span className="font-mono">SN {d.asset.serial}</span>
                {d.asset.assigned_user && <span className="flex items-center gap-0.5"><User className="h-3 w-3" />{d.asset.assigned_user}</span>}
                {d.asset.location && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{d.asset.location}</span>}
              </div>
              <Badge className={`mt-2 ${LC_TONE[d.asset.lifecycle_status] || "bg-slate-100 text-slate-700"}`}>{d.asset.lifecycle_status}</Badge>
              {d.asset.certificate_url && <a href={d.asset.certificate_url} target="_blank" rel="noreferrer" className="ml-2 text-[11px] text-sky-600 underline">Disposal certificate</a>}
            </div>
            <div>
              <div className="text-[12px] font-semibold text-slate-700 mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Lifecycle timeline</div>
              <div className="space-y-2 border-l-2 border-slate-100 pl-3">
                {d.timeline.length === 0 && <div className="text-[12px] text-slate-400">No events yet.</div>}
                {d.timeline.map((t, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-[15px] top-1 h-2 w-2 rounded-full bg-sky-500" />
                    <div className="text-[13px] text-slate-800">{t.event}</div>
                    {t.detail && <div className="text-[11px] text-slate-500">{t.detail}</div>}
                    {t.at && <div className="text-[10px] text-slate-400">{new Date(t.at).toLocaleString()}</div>}
                  </div>
                ))}
              </div>
            </div>
            {d.requests.length > 0 && (
              <div>
                <div className="text-[12px] font-semibold text-slate-700 mb-1.5">Lifecycle requests</div>
                {d.requests.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 py-1 text-[12px]">
                    <span className="font-mono text-slate-500">{r.request_no}</span>
                    <span className="text-slate-700">{TYPE_META[r.request_type]?.label || r.request_type}</span>
                    <Badge className={`ml-auto ${REQ_TONE[r.status] || ""}`}>{r.status_label || r.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- support (raise)
function SupportDialog({ asset, onClose, onDone }) {
  const [f, setF] = useState({ subject: "", description: "", category: "HARDWARE", priority: "MEDIUM" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.subject.trim()) { toast.error("Please describe the issue"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tenant-catalogue/my-assets/support", {
        serial: asset.serial, product_name: asset.product_name, order_no: asset.order_no, ...f,
      });
      toast.success(`Support request raised — ${data.ticket_no}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const Sel = ({ label, value, opts, k }) => (
    <div><label className="text-[12px] text-slate-600">{label}</label>
      <select value={value} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`support-${k}`} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 mt-1">
        {opts.map((o) => <option key={o} value={o}>{o.charAt(0) + o.slice(1).toLowerCase()}</option>)}
      </select>
    </div>
  );
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="support-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-sky-600" />Raise support</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-500">{asset.product_name} · Serial {asset.serial}</div>
        <div className="space-y-3 mt-2">
          <div><label className="text-[12px] text-slate-600">Subject *</label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="support-subject" className="h-9 mt-1" /></div>
          <div className="grid grid-cols-2 gap-2">
            <Sel label="Category" value={f.category} k="category" opts={["HARDWARE", "SOFTWARE", "WARRANTY", "DELIVERY", "OTHER"]} />
            <Sel label="Priority" value={f.priority} k="priority" opts={["LOW", "MEDIUM", "HIGH", "URGENT"]} />
          </div>
          <div><label className="text-[12px] text-slate-600">Description</label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="support-desc" className="text-sm mt-1" /></div>
          <Button onClick={submit} disabled={busy} data-testid="support-submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LifeBuoy className="h-4 w-4 mr-2" />}Submit request
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- ticket thread
function TicketDialog({ ticketId, onClose, onChange }) {
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/tenant-catalogue/my-support/${ticketId}`).then(({ data }) => setT(data)).catch(() => setT(false)), [ticketId]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post(`/tenant-catalogue/my-support/${ticketId}/comment`, { text: msg }); setMsg(""); await load(); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="ticket-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-[15px]"><LifeBuoy className="h-5 w-5 text-sky-600" />{t?.ticket_no || "Ticket"}</DialogTitle></DialogHeader>
        {!t ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div> : (
          <div className="flex flex-col overflow-hidden flex-1">
            <div className="text-sm font-medium text-slate-800">{t.subject}</div>
            <div className="flex gap-2 mt-1 mb-3 flex-wrap">
              <Badge className="bg-sky-50 text-sky-700">{t.status}</Badge>
              {t.priority && <Badge className="bg-slate-100 text-slate-600">{t.priority}</Badge>}
              {t.category && <Badge className="bg-slate-100 text-slate-600">{t.category}</Badge>}
              {t.serial && <span className="text-[11px] text-slate-400 self-center font-mono">SN {t.serial}</span>}
            </div>
            <div className="space-y-2 overflow-y-auto flex-1 pr-1">
              {(t.thread || []).length === 0 && <div className="text-[12px] text-slate-400">{t.description || "No messages yet."}</div>}
              {(t.thread || []).map((m, i) => (
                <div key={i} className={"rounded-lg px-3 py-2 text-[13px] max-w-[85%] " + (m.role === "agent" ? "bg-sky-50 text-slate-800" : "bg-slate-100 text-slate-800 ml-auto")}>
                  <div className="text-[10px] text-slate-400 mb-0.5">{m.role === "agent" ? "SUNTRONIX Support" : m.by_name || "You"} · {m.at ? new Date(m.at).toLocaleString() : ""}</div>
                  {m.text}
                </div>
              ))}
            </div>
            {!["CLOSED"].includes(t.status) && (
              <div className="flex gap-2 pt-3 border-t border-slate-100 mt-2">
                <Input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="ticket-msg" className="h-9" placeholder="Reply…" />
                <Button onClick={send} disabled={busy} data-testid="ticket-send" size="icon" className="bg-sky-600 hover:bg-sky-700 text-white shrink-0">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MyAssets() {
  const [assets, setAssets] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lifecycle, setLifecycle] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [support, setSupport] = useState(null);
  const [rma, setRma] = useState(null);
  const [lcAsset, setLcAsset] = useState(null);
  const [detail, setDetail] = useState(null);
  const [ticket, setTicket] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, t, r, lc] = await Promise.all([
        api.get("/tenant-catalogue/my-assets"), api.get("/tenant-catalogue/my-support"),
        api.get("/tenant-catalogue/my-returns"), api.get("/tenant-catalogue/my-lifecycle"),
      ]);
      setAssets(a.data.items); setTickets(t.data.items); setReturns(r.data.items); setLifecycle(lc.data.items);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load assets"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelReq = async (id) => {
    try { await api.post(`/tenant-catalogue/my-lifecycle/${id}/cancel`); toast.success("Request cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5" data-testid="my-assets-page">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Technology</div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><HardDrive className="h-6 w-6 text-sky-600" />My Assets & Warranty</h1>
        <p className="text-sm text-slate-500">Delivered devices, warranty, lifecycle and support.</p>
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div> : (
        <>
          <div className="space-y-2">
            {assets.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No delivered assets yet. Assets appear here after an order is delivered.</div>}
            {assets.map((a, i) => (
              <div key={a.serial || i} data-testid={`asset-${a.serial}`} className="rounded-lg border border-slate-200 px-4 py-3 flex flex-wrap items-center gap-3">
                <button onClick={() => setDetail(a.serial)} data-testid={`asset-open-${a.serial}`} className="flex-1 min-w-0 text-left group">
                  <div className="text-sm font-medium text-slate-800 group-hover:text-sky-700 flex items-center gap-1">{a.product_name} <span className="text-[11px] text-slate-400 font-normal">· {a.brand}</span><ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-sky-500" /></div>
                  <div className="text-[12px] text-slate-500 flex flex-wrap gap-x-3">
                    <span className="font-mono">SN {a.serial}</span>
                    {a.assigned_user && <span className="flex items-center gap-0.5"><User className="h-3 w-3" />{a.assigned_user}</span>}
                    {a.location && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{a.location}</span>}
                  </div>
                </button>
                <Badge className={LC_TONE[a.lifecycle_status] || "bg-slate-100 text-slate-700"} data-testid={`asset-lc-${a.serial}`}>{a.lifecycle_status}</Badge>
                {a.warranty && <Badge className="bg-emerald-50 text-emerald-700"><ShieldCheck className="h-3 w-3 mr-1" />{a.warranty}</Badge>}
                {a.open_request ? (
                  <Badge className="bg-amber-50 text-amber-700" data-testid={`asset-openreq-${a.serial}`}>{TYPE_META[a.open_request.type]?.label || a.open_request.type} · {a.open_request.status}</Badge>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setLcAsset(a)} data-testid={`asset-lifecycle-${a.serial}`}><Repeat className="h-3.5 w-3.5 mr-1" />Lifecycle</Button>
                    <Button size="sm" variant="outline" onClick={() => setSupport(a)} data-testid={`asset-support-${a.serial}`}><LifeBuoy className="h-3.5 w-3.5 mr-1" />Support</Button>
                    <Button size="sm" variant="outline" onClick={() => setRma(a)} data-testid={`asset-return-${a.serial}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Return</Button>
                  </>
                )}
              </div>
            ))}
          </div>

          {lifecycle.length > 0 && (
            <div className="space-y-2" data-testid="lifecycle-list">
              <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"><Repeat className="h-4 w-4 text-sky-600" />Lifecycle requests</div>
              {lifecycle.map((r) => {
                const M = TYPE_META[r.request_type] || {}; const Icon = M.icon || Repeat;
                return (
                  <div key={r.id} data-testid={`lc-row-${r.id}`} className="rounded-lg border border-slate-200 px-4 py-2.5 flex items-center gap-3">
                    <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-slate-800">{r.request_no} · {M.label || r.request_type}</div>
                      <div className="text-[11px] text-slate-400 truncate">{r.serial ? `SN ${r.serial} · ` : ""}{r.product_name}{r.target_user ? ` → ${r.target_user}` : ""}{r.target_location ? ` @ ${r.target_location}` : ""}</div>
                    </div>
                    <Badge className={REQ_TONE[r.status] || ""}>{r.status_label || r.status}</Badge>
                    {["REQUESTED", "APPROVED"].includes(r.status) && (
                      <Button size="sm" variant="ghost" onClick={() => cancelReq(r.id)} data-testid={`lc-cancel-${r.id}`} className="h-7 px-2 text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {tickets.length > 0 && (
            <div className="space-y-2" data-testid="support-tickets">
              <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"><LifeBuoy className="h-4 w-4 text-sky-600" />Support requests</div>
              {tickets.map((t) => (
                <button key={t.id} onClick={() => setTicket(t.id)} data-testid={`ticket-${t.id}`} className="w-full text-left rounded-lg border border-slate-200 px-4 py-2.5 flex items-center gap-3 hover:border-sky-300">
                  <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-slate-800 truncate">{t.ticket_no} · {t.subject}</div><div className="text-[11px] text-slate-400">{t.priority ? `${t.priority} · ` : ""}{t.serial ? `SN ${t.serial} · ` : ""}{new Date(t.created_at).toLocaleDateString()}</div></div>
                  <Badge className="bg-sky-50 text-sky-700">{t.status}</Badge>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              ))}
            </div>
          )}
          {returns.length > 0 && (
            <div className="space-y-2" data-testid="returns-list">
              <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5"><RotateCcw className="h-4 w-4 text-sky-600" />Returns & replacements</div>
              {returns.map((r) => (
                <div key={r.id} className="rounded-lg border border-slate-200 px-4 py-2.5 flex items-center gap-3">
                  <div className="flex-1 min-w-0"><div className="text-[13px] font-medium text-slate-800">{r.rma_no} · {r.rma_type}</div><div className="text-[11px] text-slate-400">{r.serial ? `SN ${r.serial} · ` : ""}{r.reason}</div></div>
                  <Badge className="bg-sky-50 text-sky-700">{r.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {support && <SupportDialog asset={support} onClose={() => setSupport(null)} onDone={() => { setSupport(null); load(); }} />}
      {rma && <RmaDialog asset={rma} onClose={() => setRma(null)} onDone={() => { setRma(null); load(); }} />}
      {lcAsset && <LifecycleDialog asset={lcAsset} onClose={() => setLcAsset(null)} onDone={() => { setLcAsset(null); load(); }} />}
      {detail && <AssetDetail serial={detail} onClose={() => setDetail(null)} />}
      {ticket && <TicketDialog ticketId={ticket} onClose={() => setTicket(null)} onChange={load} />}
    </div>
  );
}

function RmaDialog({ asset, onClose, onDone }) {
  const [rmaType, setRmaType] = useState("RETURN");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please give a reason"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tenant-catalogue/returns", {
        serial: asset.serial, product_name: asset.product_name, order_no: asset.order_no,
        rma_type: rmaType, reason,
      });
      toast.success(`Request raised — ${data.rma_no}`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="rma-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-sky-600" />Return / Replace</DialogTitle></DialogHeader>
        <div className="text-[12px] text-slate-500">{asset.product_name} · Serial {asset.serial}</div>
        <div className="space-y-3 mt-2">
          <div className="flex gap-2">
            {["RETURN", "REPLACEMENT"].map((t) => (
              <button key={t} onClick={() => setRmaType(t)} data-testid={`rma-type-${t}`}
                className={"px-3 py-1.5 rounded-md text-[12px] border " + (rmaType === t ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200")}>{t === "RETURN" ? "Return" : "Replacement"}</button>
            ))}
          </div>
          <div><label className="text-[12px] text-slate-600">Reason *</label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rma-reason" className="text-sm mt-1" placeholder="Damaged / DOA / wrong item…" /></div>
          <Button onClick={submit} disabled={busy} data-testid="rma-submit" className="w-full bg-sky-600 hover:bg-sky-700 text-white">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}Submit request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
